/* eslint-disable react-refresh/only-export-components --
 * このファイルは「ルータ定義」であってコンポーネントモジュールではない。
 * react-refresh/only-export-components は「コンポーネントを宣言しているのに
 * コンポーネント以外(= router)も export している」ファイルを検出するルールだが、
 * createBrowserRouter に渡す lazy ページ群と RootLayout はここに置くほかなく、
 * 別ファイルへ移しても router 自体の export は残るため警告は消えない(= 移す意味がない)。
 * Fast Refresh はルート定義の編集時にどのみちフルリロードが要るので実害もない。
 * eslint-plugin-react-refresh 0.5 で localComponents 検出が入り 47 件出るように
 * なったため、行ごとの disable をやめてファイル単位で明示する (PRODUCT_DEV-629)。
 */
/**
 * React Router ルーティング設定
 *
 * createBrowserRouter でルート定義、`route.lazy` でコード分割を実装
 * （PRODUCT_DEV-703 で `React.lazy` + `Suspense` から移行。理由は lazyWithReload の説明を参照）
 */

import React, { Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
} from "react-router";
import { setRouterStateReader } from "@/lib/navDiagnostics";
import { lazyWithReload } from "@/lib/routeLazy";
import { rootLoader } from "@/loaders/root-loader";
import { shouldRevalidateRoot } from "@/loaders/shouldRevalidateRoot";
import { MasterDataProvider } from "@/contexts/MasterDataContext";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { ChromeProvider } from "./components/shared/chrome/ChromeContext";
import { unizoChrome } from "./components/shared/chrome/unizoChrome";
import { Splash } from "./components/shared/Splash";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import { EnvironmentBanner } from "./components/shared/EnvironmentBanner";
import { NavigationWatchdog } from "./components/shared/NavigationWatchdog";
import { NavigationProgress } from "./components/shared/NavigationProgress";
import { SysadminGuard } from "./components/shared/SysadminGuard";
import { RoleSplitDescendedGuard } from "./components/shared/RoleSplitDescendedGuard";
import {
  ROUTE_COMPANY_REPORT,
  ROUTE_MY_CUSTOMERS,
  ROUTE_MY_CUSTOMERS_DETAIL,
  ROUTE_GUEST_SCHEDULING,
} from "@/utils/const";

// ─────────────────────────────────────────────────────────────
// Lazy-loaded Pages
// ─────────────────────────────────────────────────────────────

const Recording = lazyWithReload(() => import("./pages/Recording/index"));
const SpeedRecording = lazyWithReload(() => import("./pages/SpeedRecording/index"));
const CopyRecordSetup = lazyWithReload(() => import("./pages/CopyRecordSetup/index"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard/index"));
const MeetingDetail = lazyWithReload(() => import("./pages/MeetingDetail/index"));
const MeetingGroupCreate = lazyWithReload(
  () => import("./pages/MeetingGroupCreate"),
);
const MeetingGroupDetail = lazyWithReload(
  () => import("./pages/MeetingGroupDetail"),
);
const Guide = lazyWithReload(() => import("./pages/Guide/index"));
const GuideArticleDetail = lazyWithReload(
  () => import("./pages/Guide/ArticleDetail"),
);
const Announcements = lazyWithReload(
  () => import("./pages/Announcements/index"),
);
const Trash = lazyWithReload(() => import("./pages/Trash/index"));
const Notifications = lazyWithReload(() => import("./pages/Notifications/index"));
// PRODUCT_DEV-635: 問い合わせ
const Inquiries = lazyWithReload(() => import("./pages/Inquiries/index"));
// PRODUCT_DEV-673: マイページ（導線なし・URL 直打ちで到達）
const MyPage = lazyWithReload(() => import("./pages/MyPage/index"));
const ActivitySummaryScreen = lazyWithReload(
  () => import("./pages/MyPage/Summary/index"),
);
const ArchiveScreen = lazyWithReload(() => import("./pages/MyPage/Archive/index"));
const SchedulingListScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/index"),
);
const SchedulingDetailScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Detail"),
);
const SchedulingRescheduleScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Reschedule"),
);
const SchedulingEditScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Edit"),
);
const SchedulingNewScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/New"),
);
const GuestSchedulingScreen = lazyWithReload(
  () => import("./pages/GuestScheduling/index"),
);
const ReportScreen = lazyWithReload(() => import("./pages/MyPage/Report/index"));
const AdminSummaryScreen = lazyWithReload(
  () => import("./pages/MyPage/Admin/index"),
);
const InquiryNew = lazyWithReload(() => import("./pages/Inquiries/new"));
// PRODUCT_DEV-690: 問い合わせ前の AI Q&A（flag inquiryQa）
const InquiryQa = lazyWithReload(() => import("./pages/Inquiries/qa"));
const InquiryDetail = lazyWithReload(
  () => import("./pages/Inquiries/[inquiryId]"),
);
const Settings = lazyWithReload(() => import("./pages/Settings/index"));
const ShortcutInstall = lazyWithReload(
  () => import("./pages/ShortcutInstall/index"),
);
const Chat = lazyWithReload(() => import("./pages/Chat"));
const Memo = lazyWithReload(() => import("./pages/Memo"));
const MemoDetail = lazyWithReload(() => import("./pages/Memo/MemoDetail"));
const MemoTodoDetail = lazyWithReload(() => import("./pages/Memo/MemoTodoDetail"));
const MemoSearch = lazyWithReload(() => import("./pages/Memo/MemoSearch"));
const MemoRecording = lazyWithReload(() => import("./pages/Memo/MemoRecording"));
// PRODUCT_DEV-583: マイ顧客（マイページ）
const MyCustomers = lazyWithReload(() => import("./pages/MyCustomers/index"));
const MyCustomerDetail = lazyWithReload(() => import("./pages/MyCustomers/[companyId]/index"));
const MyCustomerEdit = lazyWithReload(() => import("./pages/MyCustomers/[companyId]/edit"));
const MyCustomersHandover = lazyWithReload(() => import("./pages/MyCustomers/handover"));
// PRODUCT_DEV-586: 議事録
const MinutesRecording = lazyWithReload(
  () => import("./pages/Minutes/MinutesRecording"),
);
const MinutesDetail = lazyWithReload(() => import("./pages/Minutes/MinutesDetail"));
const AdminLayout = lazyWithReload(() => import("./components/shared/AdminLayout"));
const AdminTop = lazyWithReload(() => import("./pages/admin/AdminTop"));
const Users = lazyWithReload(() => import("./pages/admin/Users"));
const UserDetail = lazyWithReload(() => import("./pages/admin/Users/[userId]"));
const TenantSettings = lazyWithReload(() => import("./pages/admin/TenantSettings"));
const AdminPresets = lazyWithReload(() => import("./pages/admin/Presets"));
const AdminPresetDetail = lazyWithReload(() => import("./pages/admin/Presets/[presetId]"));
// PRODUCT_DEV-684: 音声でプリセットを作る（自由記録ベースの往復）
const AdminPresetDraftBuilder = lazyWithReload(() => import("./pages/admin/Presets/DraftBuilder"));
const AdminCompanies = lazyWithReload(() => import("./pages/admin/Companies"));
const AdminCompanyDetail = lazyWithReload(() => import("./pages/admin/Companies/[companyId]"));
const CompanyReport = lazyWithReload(() => import("./pages/admin/Companies/[companyId]/Report"));
const AdminDictionaries = lazyWithReload(() => import("./pages/admin/Dictionaries"));
const AdminDictionaryDetail = lazyWithReload(() => import("./pages/admin/Dictionaries/[dictionaryId]"));
const AdminRealtimeConfigs = lazyWithReload(() => import("./pages/admin/RealtimeConfigs"));
// PRODUCT_DEV-572: 部署管理
const AdminDepartments = lazyWithReload(() => import("./pages/admin/Departments"));
// PRODUCT_DEV-640: データ出力（商談結果DL）。sysadmin メニューから遷移。
const AdminDataExport = lazyWithReload(() => import("./pages/admin/DataExport"));
const AdminRealtimeConfigDetail = lazyWithReload(() => import("./pages/admin/RealtimeConfigs/[configId]"));
const Forbidden = lazyWithReload(() => import("./pages/403"));
const NotFound = lazyWithReload(() => import("./pages/404"));

// ─────────────────────────────────────────────────────────────
// Layout Component
// ─────────────────────────────────────────────────────────────
const RootLayout: React.FC = () => {
  return (
    // PRODUCT_DEV-748: 下部ナビ・メニュー・ロゴの実体をここで注入する。
    // Minizo ビルドは別エントリ（router.minizo.tsx）が minizoChrome を注入するため、
    // Unizo 側のナビ・メニューは Minizo の dist に入らない。
    <ChromeProvider value={unizoChrome}>
    <div className="min-h-screen bg-slate-50">
      <EnvironmentBanner />
      <ScrollToTop />
      {/* PRODUCT_DEV-612: 遷移がコミットされないまま固まったらフルページロードで脱出する。
          Outlet の外（Suspense の外）に置くこと。中に置くと遷移先と一緒にサスペンドして
          ウォッチドッグ自身が動かなくなる。 */}
      <NavigationWatchdog />
      {/* PRODUCT_DEV-703: 遷移中の唯一のフィードバック（route.lazy は現在の画面を出したまま
          保留するため、これが無いと「押しても何も起きない」に見える）。Suspense の外に置く。 */}
      <NavigationProgress />
      {/* PRODUCT_DEV-652: 企業/ユーザマスタは loader を待たせず、シェル描画後に取得する。
          Provider をここ（loader の内側 = auth 解決済み）に置くことで tenantId が確実に得られる。 */}
      {/* PRODUCT_DEV-699: 個人設定はサーバが正。MasterData と同じく loader の内側
          （auth 解決済み）に置く。初期値は localStorage の下書きから同期的に決まるので、
          ここでサスペンドはしない。 */}
      <PreferencesProvider>
        <MasterDataProvider>
          {/* PRODUCT_DEV-703: ページは route.lazy へ移したので通常はここでサスペンドしない。
              ページ内部の React.lazy / use() に対する安全網として残す。 */}
          <Suspense fallback={<Splash />}>
            <Outlet />
          </Suspense>
        </MasterDataProvider>
      </PreferencesProvider>
    </div>
    </ChromeProvider>
  );
};

// PRODUCT_DEV-583 R5/614: メンバー向け顧客マスタは「顧客一覧」(/my-customers) に
// 統合済み（100%反映）のため、/companies 系は常に顧客一覧へリダイレクトする
// （:companyId 付きは対応する顧客詳細へ）。/admin/companies は対象外。
const LegacyCompaniesRoute: React.FC = () => {
  const { companyId } = useParams();
  const to = companyId
    ? ROUTE_MY_CUSTOMERS_DETAIL.replace(":companyId", encodeURIComponent(companyId))
    : ROUTE_MY_CUSTOMERS;
  return <Navigate to={to} replace />;
};

// PRODUCT_DEV-622: 顧客レポートを管理画面レイアウトの外（単体ページ）へ移した。
// 既存のブックマーク・過去の商談詳細からの戻り先が死なないようリダイレクトを残す。
const LegacyReportRoute: React.FC = () => {
  const { companyId } = useParams();
  if (!companyId) return <Navigate to={ROUTE_MY_CUSTOMERS} replace />;
  return (
    <Navigate
      to={ROUTE_COMPANY_REPORT.replace(":companyId", encodeURIComponent(companyId))}
      replace
    />
  );
};

// ─────────────────────────────────────────────────────────────
// Router Configuration
// ─────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  {
    path: import.meta.env.BASE_URL,
    id: "root",
    loader: rootLoader,
    // PRODUCT_DEV-652 / 702 / 703: react-router の既定ではナビゲーションのたびに loader が
    // 再実行される。root-loader は whoami + 設定系 6 本（presets 261kB 等）を取り、
    // loader は遷移をブロックするため、そのまま体感の待ち時間になっていた。
    // 現在は「遷移では取り直さず、マスタを書き換えたときに取り直す」に統一している
    // （判定とテストは shouldRevalidateRoot.ts / loaders/rootMasters.ts を参照）。
    shouldRevalidate: (args) => shouldRevalidateRoot(args),
    element: <RootLayout />,
    children: [
      { index: true, lazy: Dashboard },
      { path: "recording", lazy: Recording },
      { path: "speed-recording", lazy: SpeedRecording },
      { path: "copy-record-setup", lazy: CopyRecordSetup },
      { path: "meetings/:id", lazy: MeetingDetail },
      { path: "meeting-groups/create", lazy: MeetingGroupCreate },
      { path: "meeting-groups/:groupId", lazy: MeetingGroupDetail },
      { path: "guide", lazy: Guide },
      {
        path: "guide/article/:categoryId/:articleId",
        lazy: GuideArticleDetail,
      },
      { path: "announcements", lazy: Announcements },
      { path: "trash", lazy: Trash },
      { path: "notifications", lazy: Notifications },
      // PRODUCT_DEV-635: 問い合わせ（静的パス new を動的パスより先に定義）
      { path: "mypage", lazy: MyPage },
      { path: "mypage/summary", lazy: ActivitySummaryScreen },
      { path: "mypage/archive", lazy: ArchiveScreen },
      // PRODUCT_DEV-675: 静的セグメントを :scheduleId より先に置く（既存の慣習どおり）
      { path: "mypage/scheduling", lazy: SchedulingListScreen },
      // 静的セグメントを :scheduleId より先に置く
      { path: "mypage/scheduling/new", lazy: SchedulingNewScreen },
      { path: "mypage/scheduling/:scheduleId", lazy: SchedulingDetailScreen },
      {
        // 確定後の日時変更（PRODUCT_DEV-675 PR9）。詳細から遷移する
        path: "mypage/scheduling/:scheduleId/reschedule",
        lazy: SchedulingRescheduleScreen,
      },
      {
        // 確定前の候補日時の編集（PRODUCT_DEV-675）。詳細から遷移する
        path: "mypage/scheduling/:scheduleId/edit",
        lazy: SchedulingEditScreen,
      },
      { path: "mypage/admin", lazy: AdminSummaryScreen },
      // 静的セグメントより後ろに置く（router.tsx の慣習）
      {
        path: "mypage/reports/:period/:periodKey",
        lazy: ReportScreen,
      },
      { path: "inquiries", lazy: Inquiries },
      { path: "inquiries/new", lazy: InquiryNew },
      // 静的パスは動的パス（:inquiryId）より先に置く
      { path: "inquiries/qa", lazy: InquiryQa },
      { path: "inquiries/:inquiryId", lazy: InquiryDetail },
      { path: "settings", lazy: Settings },
      { path: "shortcut-install", lazy: ShortcutInstall },
      { path: "chat", lazy: Chat },
      { path: "memo", lazy: Memo },
      { path: "memo/search", lazy: MemoSearch },
      { path: "memo/recording", lazy: MemoRecording },
      { path: "memo/todo/:todoId", lazy: MemoTodoDetail },
      { path: "memo/:memoId", lazy: MemoDetail },
      // PRODUCT_DEV-583: マイ顧客（マイページ）。handover を :companyId より前に登録。
      { path: "my-customers", lazy: MyCustomers },
      { path: "my-customers/handover", lazy: MyCustomersHandover },
      { path: "my-customers/:companyId", lazy: MyCustomerDetail },
      { path: "my-customers/:companyId/edit", lazy: MyCustomerEdit },
      // PRODUCT_DEV-586: 議事録（録音は静的パス→動的パスより先に定義）
      { path: "minutes/recording", lazy: MinutesRecording },
      { path: "minutes/:minutesId", lazy: MinutesDetail },
      // PRODUCT_DEV-583 R5/614: メンバー向け /companies は顧客一覧へ常時リダイレクト
      // （admin/companies は下の admin ルートで従来どおり維持）。
      // PRODUCT_DEV-622: 顧客レポート（管理メニューを持たない単体ページ）
      { path: "customer-report/:companyId", lazy: CompanyReport },
      { path: "companies", element: <LegacyCompaniesRoute /> },
      { path: "companies/:companyId", element: <LegacyCompaniesRoute /> },
      // PRODUCT_DEV-419 follow-up: メンバー向け辞書管理（admin/dictionariesと同じコンポーネントを共有）
      { path: "dictionaries", lazy: AdminDictionaries },
      { path: "dictionaries/:dictionaryId", lazy: AdminDictionaryDetail },
      {
        path: "admin",
        lazy: AdminLayout,
        children: [
          { index: true, lazy: AdminTop },
          // admin向け（一般管理者でもアクセス可能）
          { path: "companies", lazy: AdminCompanies },
          { path: "companies/:companyId", lazy: AdminCompanyDetail },
          { path: "companies/:companyId/report", element: <LegacyReportRoute /> },
          { path: "dictionaries", lazy: AdminDictionaries },
          { path: "dictionaries/:dictionaryId", lazy: AdminDictionaryDetail },
          // PRODUCT_DEV-572: 部署管理はテナント管理者(admin)に解放
          { path: "departments", lazy: AdminDepartments },
          // PRODUCT_DEV-721: ユーザー管理・パスワード再設定・データ出力をテナント管理者(admin)へ
          // 降ろした（9/8 プロダクト要件会議）。**機能フラグ `managerRole` が ON のテナントだけ**で、
          // OFF のあいだは従来どおり sysadmin 限定（ガードが切り替える）。
          // ⚠ specialFlags の付与はフラグに関わらず sysadmin 限定（画面・API の両方で弾く）。
          {
            element: <RoleSplitDescendedGuard />,
            children: [
              { path: "users", lazy: Users },
              { path: "users/:userId", lazy: UserDetail },
              { path: "data-export", lazy: AdminDataExport },
            ],
          },
          // sysadmin限定
          {
            element: <SysadminGuard />,
            children: [
              { path: "tenant-settings", lazy: TenantSettings },
              { path: "presets", lazy: AdminPresets },
              // 静的セグメントは :presetId より優先されるため順序に依存しない
              { path: "presets/voice", lazy: AdminPresetDraftBuilder },
              { path: "presets/new", lazy: AdminPresetDetail },
              { path: "presets/:presetId", lazy: AdminPresetDetail },
              { path: "realtime-configs", lazy: AdminRealtimeConfigs },
              { path: "realtime-configs/new", lazy: AdminRealtimeConfigDetail },
              { path: "realtime-configs/:configId", lazy: AdminRealtimeConfigDetail },
            ],
          },
        ],
      },
      {
        path: "403",
        lazy: Forbidden,
      },
      {
        path: "*",
        lazy: NotFound,
      },
    ],
  },
  // PRODUCT_DEV-675: 相手（未ログイン）の回答画面 — **rootLoader の外に置く唯一のルート**。
  //
  // root の children に入れると rootLoader が走り、未ログインの相手が Portal SSO へ
  // 飛ばされてしまう。「相手がログインせずに応答できる」ことがこの機能の価値そのもの
  // なので、loader を持たない兄弟ルートとして並べる。
  //
  // ⚠ この配下のコンポーネントは `components/shared/` を一切使えない
  //   （`MobileLayout` 等が `useRootLoader()` を呼ぶため、root context が無いと落ちる）。
  //
  // CloudFront は /assets 等以外を /index.html に書き換えるので CDK 側の追加設定は不要。
  // 認可はワンタイムトークンが担う（API も /public/scheduling/* だけが Authorizer 無し）。
  {
    path: ROUTE_GUEST_SCHEDULING,
    lazy: GuestSchedulingScreen,
  },
]);

/**
 * PRODUCT_DEV-612: ウォッチドッグ発火時に react-router 自身の状態を読めるようにする。
 *
 * フック（`useLocation` / `useNavigation`）では取れない。遷移がコミットされないときは
 * フックの値も同じく更新されないため、「react-router がまだ解決していない」のか
 * 「react-router は完了していて React がコミットしていない」のかを区別できない。
 * `router.state` は React のコミットとは無関係に同期更新されるので、ここを直接読む。
 *
 * 注入式にしているのは、`navigationWatchdog` が `router.tsx` を import すると
 * 循環参照になるため（`router.tsx` → `NavigationWatchdog` → `navigationWatchdog`）。
 */
setRouterStateReader(() => {
  const { navigation, location, historyAction } = router.state;
  return {
    navigationState: navigation.state,
    navigatingTo: navigation.location
      ? navigation.location.pathname + navigation.location.search
      : null,
    routerLocation: location.pathname + location.search,
    historyAction: historyAction ?? null,
  };
});
